/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { Button } from "@chakra-ui/react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import { Tooltip, IconButton } from "@mui/material";
import { MdOutlineContentCopy, MdOutlineCheckCircle } from "react-icons/md";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import axios from "axios";
import InputField from "components/fields/InputField";

const rowPerPage = 10;

type MerchantData = {
  id: string;
  name: string;
  created_at: string;
};

interface MerchantListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (merchantData: MerchantData) => void;
  entityType?: string;
}

interface PayloadType {
  [key: string]: any;
}

const initialFilters = {
  name: "",
};

const MerchantListModal: React.FC<MerchantListModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  entityType,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<MerchantData[]>([]);

  // Filter state
  const [filters, setFilters] = useState(initialFilters);

  // Pagination state
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(rowPerPage);
  const [rowCount, setRowCount] = useState<number>(rowPerPage);

  // Fetch when modal opens
  useEffect(() => {
    if (isOpen) {
      setTableData([]);
      setPage(0);
      setLastKeys({});
      setCurrentLastKey("");
      setRowCount(rowPerPage);
      setFilters(initialFilters);
      fetchMerchants({
        page: 0,
        limit: pageSize,
        lastkey: "",
        ...initialFilters,
      });
    }
  }, [isOpen]);

  const handleInputChange = (event: any) => {
    const { id, value } = event.target;
    setFilters({ ...filters, [id]: value });
  };

  const handleSearchSubmit = () => {
    setPage(0);
    setLastKeys({});
    setCurrentLastKey("");
    fetchMerchants({
      page: 0,
      limit: pageSize,
      lastkey: "",
      ...filters,
    });
  };

  const handleSearchClear = () => {
    setFilters(initialFilters);
    setPage(0);
    setLastKeys({});
    setCurrentLastKey("");
    fetchMerchants({
      page: 0,
      limit: pageSize,
      lastkey: "",
      ...initialFilters,
    });
  };

  const fetchMerchants = async (payload: PayloadType = {}) => {
    try {
      setIsLoading(true);

      const queryObject: PayloadType = {
        limit: payload?.limit || pageSize,
        lastkey: payload?.lastkey || "",
      };

      // Add filter parameters
      if (payload?.name) {
        queryObject.name = payload.name;
      }
      if (entityType) {
        queryObject.entity_type = entityType;
      }

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/merchant/all-merchants?${queryString}`
      );

      const newData: MerchantData[] = response.data.merchants?.payload || [];

      if (newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      // Manage Last Key and Total Record
      const newLastKey = response.data.merchants?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        const newPage = payload?.page || 0;
        const newPageSize = payload?.limit || pageSize;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys((prev) => ({ ...prev, [newPage]: newLastKey }));
      } else {
        // No more pages
        const newPage = payload?.page || 0;
        const newPageSize = payload?.limit || pageSize;
        setRowCount(newPage * newPageSize + newData.length);
        setCurrentLastKey("");
      }
    } catch (error) {
      console.error("Error fetching merchants", error);
      setTableData([]);
      // Clear all
      setPage(0);
      setRowCount(0);
      setLastKeys({});
      setCurrentLastKey("");
    } finally {
      setIsLoading(false);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setIsLoading(true);

    let newPayload: PayloadType = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      ...filters,
    };

    // If click prev pagination
    if (newValue.page < page) {
      newPayload = {
        ...newPayload,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    // if select new row per page, reset page to 0, reset lastkey
    if (newValue.pageSize !== pageSize) {
      newPayload = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        ...filters,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys({});
    } else {
      setPage(newValue.page);
    }

    fetchMerchants(newPayload);
  };

  const handleSelect = (merchant: MerchantData) => {
    onSelect(merchant);
    onClose();
  };

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "",
      flex: 1,
      minWidth: 50,
      maxWidth: 50,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<MerchantData, string>) => (
        <Tooltip title="Copy Merchant ID" arrow placement="top">
          <div>
            <IconButton
              id={"button-copy-merchantid-" + params?.row?.id}
              onClick={() => handleClickCopy(params.row.id)}
            >
              <MdOutlineContentCopy className="h-5 w-5 text-navy-800" />
            </IconButton>
          </div>
        </Tooltip>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 200,
      sortable: false,
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      flex: 1,
      minWidth: 100,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        return (
          <Tooltip title="Select Merchant" arrow placement="top">
            <IconButton
              id={"button-select-" + params?.row?.id}
              onClick={() => handleSelect(params.row)}
            >
              <MdOutlineCheckCircle className="h-5 w-5" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const customDataGridStyles: Partial<GridClasses> = {
    root: "mui-table-custom",
    columnHeader: "text-start",
    row: "",
  };

  return (
    <>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          closeOnOverlayClick={false}
          size="4xl"
        >
          <ModalOverlay className="bg-[#000] !opacity-30" />
          <ModalContent className="dark:!bg-neutral-900 !z-[9999] !m-auto !w-full !max-w-[90%] !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[3vh] lg:!max-w-[800px]">
            <ModalBody className="p-0">
              <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-white/10 md:px-7">
                <div className="flex items-start gap-3">
                  <div className="mt-1 rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-300 dark:ring-primary-900/30">
                    <svg
                      viewBox="0 0 24 24"
                      className="h-6 w-6"
                      fill="currentColor"
                    >
                      <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Select Merchant
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Select a merchant to filter payouts
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-3xl border border-gray-200 text-gray-600 transition hover:bg-gray-50 hover:text-gray-700 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
                  aria-label="Close"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18 6 6 18" />
                    <path d="M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body (scroll) */}
              <div className="max-h-[70vh] overflow-y-auto">
                {/* Sticky Filter */}
                <div className="dark:bg-neutral-900/85 sticky top-0 z-10 border-b border-gray-100 bg-white/90 px-6 py-4 backdrop-blur dark:border-white/10 md:px-7">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div className="w-full sm:max-w-[580px]">
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                          <svg
                            viewBox="0 0 24 24"
                            className="h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                          </svg>
                        </span>
                        <input
                          disabled={isLoading}
                          id="name"
                          type="text"
                          value={filters.name}
                          onChange={handleInputChange}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleSearchSubmit();
                            }
                          }}
                          placeholder="Search by merchant name…"
                          className="dark:bg-neutral-800 h-10 w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 text-sm font-medium text-gray-900 shadow-sm outline-none transition focus:border-navy-400 focus:ring-2 focus:ring-navy-200 disabled:cursor-not-allowed disabled:opacity-60"
                        />
                      </div>
                    </div>

                    <div className="flex gap-2 sm:justify-end">
                      <Button
                        isDisabled={isLoading || !filters.name}
                        onClick={handleSearchClear}
                        className="dark:bg-neutral-900 h-10 rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-60 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                      >
                        Clear
                      </Button>

                      <Button
                        isDisabled={isLoading || !filters.name?.trim()}
                        onClick={handleSearchSubmit}
                        isLoading={isLoading}
                        loadingText="Searching..."
                        className="btn-primary h-10 rounded-lg px-5 text-sm font-semibold shadow-sm disabled:opacity-60"
                      >
                        Search
                      </Button>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Tip: Enter a keyword and click Search.
                  </div>
                </div>

                {/* Table */}
                <div className="px-6 py-5 md:px-7">
                  <Box className="inner-border w-full overflow-hidden rounded-b-xl rounded-t-lg bg-white">
                    <DataGrid
                      sx={{
                        "& .MuiDataGrid-cell": { padding: "8px 16px" },
                        "& .MuiDataGrid-columnHeader": { padding: "8px 16px" },
                        "& .MuiTablePagination-input": { marginRight: "-20px" },
                        "& .MuiTablePagination-displayedRows": {
                          display: "none",
                        },
                        border: "none",
                      }}
                      loading={isLoading}
                      rows={tableData || []}
                      columns={columns}
                      getRowId={(row) => row.id}
                      rowCount={rowCount}
                      sortingMode="server"
                      paginationMode="server"
                      pageSizeOptions={[10]}
                      rowHeight={48}
                      columnHeaderHeight={48}
                      paginationModel={{ page: page, pageSize: pageSize }}
                      onPaginationModelChange={setPaginationModel}
                      disableRowSelectionOnClick
                      disableColumnFilter
                      disableColumnMenu
                      classes={customDataGridStyles}
                      hideFooter={currentLastKey === "" && page === 0}
                      autoHeight
                      localeText={{
                        noRowsLabel: "No merchants found",
                      }}
                    />
                  </Box>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-6 py-4 dark:border-white/10 md:px-7">
                <Button
                  type="button"
                  onClick={onClose}
                  className="dark:bg-neutral-900 h-10 rounded-lg border border-gray-200 bg-white px-5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                >
                  Close
                </Button>
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default MerchantListModal;
